/**
 * Stop Utilities - Helper functions for stop display and processing
 * Following patterns from main B18 app (loadNew.ts, loadnew-utils.tsx)
 */

import { LoadStop, StopType } from '@/types/load'
import { canadaProvinces, StateTimezone, usStates } from '@/utils/states'
import { defaultValues } from '@/utils/constants'
import { formatScheduleWindow } from '@/utils/dateConversion'

/**
 * Get stop type label for display
 * Pattern from main app: getStopTypeLabel in loadNew.ts
 * @example
 * getStopTypeLabel('pickup') // "Pickup"
 * getStopTypeLabel('transit-drop') // "Transit Drop"
 */
export const getStopTypeLabel = (stopType?: string | null): string => {
  const labels: Record<string, string> = {
    pickup: 'Pickup',
    drop: 'Drop',
    'transit-drop': 'Transit Drop',
    'transit-pickup': 'Transit Pickup',
    'empty-move': 'Empty Move'
  }
  return labels[stopType || ''] || 'Stop'
}

/**
 * Get MUI color for stop type
 * @returns Color name for MUI Chip component
 */
export const getStopTypeColor = (
  stopType?: string | null
): 'primary' | 'error' | 'warning' | 'info' | 'default' => {
  if (!stopType) return 'default'
  if (stopType.includes('pickup')) return 'primary'
  if (stopType.includes('drop')) return 'error'
  if (stopType === 'empty-move') return 'warning'
  if (stopType.includes('transit')) return 'info'
  return 'default'
}

/**
 * Check if stop is an empty move stop
 * Pattern from main app: isEmptyMoveStop in loadNew.ts
 */
export const isEmptyMoveStop = (stop: LoadStop): boolean => {
  return stop.isEmptyMoveStop === true || stop.stopType === 'empty-move'
}

/**
 * Get normalized stop type (handles legacy 'type' field)
 * Main app uses 'stopType' but some data may have 'type'
 */
export const getNormalizedStopType = (stop: LoadStop): StopType | string => {
  // Prefer stopType, fall back to type
  if (stop.stopType) return stop.stopType
  if (stop.type) {
    // Map legacy type values to new format
    const typeMap: Record<string, StopType> = {
      pickup: 'pickup',
      drop: 'drop',
      transit: 'transit-drop' // legacy 'transit' maps to transit-drop
    }
    return typeMap[stop.type] || stop.type
  }
  return 'pickup' // default
}

/**
 * Get all stops sorted by index (including empty move stops)
 * Pattern from main app: getStopsToRender in loadnew-utils.tsx
 * Empty move stops are placed first, then regular stops sorted by index
 */
export const getAllStopsSorted = (
  stops?: LoadStop[] | null,
  emptyMoveStops?: LoadStop[] | null
): LoadStop[] => {
  const allStops: LoadStop[] = []

  // Add empty move stops first (with stopType set)
  if (emptyMoveStops?.length) {
    allStops.push(
      ...emptyMoveStops.map(s => ({
        ...s,
        stopType: 'empty-move' as StopType
      }))
    )
  }

  // Add regular stops
  if (stops?.length) {
    allStops.push(...stops)
  }

  // Sort by index (empty moves typically have index -1 or 0)
  return allStops.sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
}

/**
 * Get stop status for display
 * Based on check-in/out times
 */
export const getStopStatus = (
  stop: LoadStop
): { label: string; color: 'success' | 'warning' | 'default' | 'info' } => {
  // Check for checkOut (completed)
  if (stop.checkOut || stop.checkedOut) {
    return { label: 'Completed', color: 'success' }
  }
  // Check for checkIn (at facility)
  if (stop.checkIn || stop.checkedIn) {
    return { label: 'At Facility', color: 'warning' }
  }
  // Default pending
  return { label: 'Pending', color: 'default' }
}

/**
 * Get stop number for display (handles empty move numbering)
 * Empty moves show as "EM-1", regular stops show as "1", "2", etc.
 * Note: stop.index is 0-based in data, so we add 1 for display
 */
export const getStopNumber = (stop: LoadStop, stopIndex: number): string => {
  if (isEmptyMoveStop(stop)) {
    return stop.emptyMoveSerialNumber || `EM-${stopIndex + 1}`
  }
  // Regular stops: use index field if available (add 1 since index is 0-based)
  // otherwise use array position + 1
  const num = stop.index !== undefined ? stop.index + 1 : stopIndex + 1
  return `${num}`
}

/**
 * The zone name of a stored timezone.
 * Pattern from main app: toTimezoneName in getTimeZone.ts
 * Since P2-1636 a facility timezone can be { value, source, derivedAt }; older records keep the plain name.
 * Anything that is not a non-empty string after unwrapping counts as missing.
 */
const toTimezoneName = (timezone: unknown): string | undefined => {
  if (typeof timezone === 'string') return timezone || undefined
  if (timezone && typeof timezone === 'object' && 'value' in timezone) {
    const { value } = timezone
    return typeof value === 'string' && value ? value : undefined
  }
  return undefined
}

/**
 * The zone of a US state or Canadian province, matched by full name or code.
 * Pattern from main app: getTimeZoneUtil in getTimeZone.ts (United States and Canada only)
 */
const getStateTimezone = (
  country?: string,
  state?: string
): string | undefined => {
  if (!country || !state) return undefined
  const matches = (item: StateTimezone) =>
    item.label === state || item.code === state
  if (country === 'Canada') return canadaProvinces.find(matches)?.timezone
  if (country === 'United States') {
    // The web's usaStates list holds the provinces too
    return [...usStates, ...canadaProvinces].find(matches)?.timezone
  }
  return undefined
}

/**
 * Get timezone from stop address details
 * Pattern from main app: getTimeZoneUtil in getTimeZone.ts
 * The shown address's own zone first, else the zone of its state or province
 */
export const getStopTimezone = (stop: LoadStop): string | undefined => {
  // Determine data source based on hideAddress flag
  const dataSource = stop.hideAddress
    ? stop.cityStateAddressDetails
    : stop.facilityDetails
  const addressSource = stop.hideAddress
    ? stop.cityStateAddressDetails
    : stop.facilityDetails?.addressDetails

  return (
    toTimezoneName(dataSource?.timezone) ??
    getStateTimezone(addressSource?.country, addressSource?.state)
  )
}

/**
 * The zone for a stop's check-in and check-out times.
 * Main app: EmptyMoveStopDetails.tsx uses the empty move's own location zone,
 * then its city/state zone, then the normal stop zone, then America/New_York.
 * Other stops use the normal stop zone, with the same default.
 */
export const getStopCheckTimezone = (stop: LoadStop): string => {
  const emptyMoveZone = isEmptyMoveStop(stop)
    ? (toTimezoneName(stop.emptyMoveLocationData?.timezone) ??
      toTimezoneName(stop.cityStateAddressDetails?.timezone))
    : undefined
  return (
    emptyMoveZone ?? getStopTimezone(stop) ?? defaultValues.DEFAULT_TIMEZONE
  )
}

/** The web's text where a stop's schedule would be, when needsActiveAppointment */
export const NO_ACTIVE_APPOINTMENT = 'No active appointment'

/**
 * True when a stop has two or more appointment options and none is Active.
 * The backend clears the stop's schedule in that state, so the web shows
 * 'No active appointment' instead (P2-1243).
 * Pattern from main app: needsActiveAppointment in appointmentOptionUtils.ts
 */
export const needsActiveAppointment = (stop?: LoadStop | null): boolean => {
  const options = stop?.appointmentOptions
  return (
    Array.isArray(options) &&
    options.length > 1 &&
    !options.some(option => option?.active)
  )
}

/**
 * A stop's schedule text, as the web's Dispatch tab shows it: start - end,
 * with 'Not Scheduled' for a missing side, or 'No active appointment'.
 */
export const formatStopSchedule = (
  stop: LoadStop,
  timezone: string | undefined = getStopTimezone(stop)
): string =>
  needsActiveAppointment(stop)
    ? NO_ACTIVE_APPOINTMENT
    : formatScheduleWindow(stop.scheduleStart, stop.scheduleEnd, timezone)

/**
 * Check if stop has schedule times
 */
export const hasSchedule = (stop: LoadStop): boolean => {
  return !!(stop.scheduleStart?.seconds || stop.appointmentStart)
}

/**
 * Check if stop has check-in/out times
 */
export const hasCheckTimes = (stop: LoadStop): boolean => {
  return !!(stop.checkIn || stop.checkedIn || stop.checkOut || stop.checkedOut)
}
